/* The pack count (blocks/st-pack-picker.liquid).
   - Since 2026-10-10 the homepage buy box's counter: the number field and its -/+ take any count to
     9,999, and a quick pick per pack size jumps to it, showing that pack's saving against single
     units (the smallest pack shows its price). The v5 slider's code stays for a range input, if a
     picker still renders one: 1 to 10 one by one, then sensible steps, up to the largest pack.
   - Each count is priced as the cheapest mix of the available packs (a small knapsack over the
     pack sizes; ties go to fewer lines), and the pack total in the same product details is
     rewritten from it: price, saving against single units, price per unit, and "Sent as ..." for a
     mix. The price counts to its new value as before (countUp, in assets/st-money.js).
   - Horizon's variant is not switched (on the homepage its variant fetch 404s). Instead:
     Add to cart is taken before Horizon's own handler (a window capture listener runs before
     Horizon's document one) and the mix is posted to /cart/add.js as lines, then announced as a
     CartLinesUpdateEvent so the drawer opens and redraws exactly as for Horizon's own add; and
     Horizon's Buy it now is replaced by a link to a cart permalink (/cart/id:qty,...), which opens
     checkout with exactly this mix.
   - A size or colour chosen in Horizon's picker re-prices the same count from the new variant's
     packs, read from the section Horizon fetches for it.
   - The count is kept per product across any re-render of the product details.
   - Every count announces its mix as an st:pack-mix event (product, selected variant, currency,
     count, total and lines, prices in hundredths), the first one per picker marked initial.
     assets/st-track.js publishes st_pack_changed from it once the count settles, so Meta's
     ViewContent carries the mix the buyer sees (TRACKING-QA.md, Matt 2026-10-16); mixDetail() gives
     the same for a platform swap's event (assets/st-platform-swap.js). */
// A module, so it runs once a page however many blocks include it (money and the count-up come from
// assets/st-money.js). The counts live on window, where assets/st-platform-swap.js carries one across a swap.
import { money, countUp } from '@theme/st-money';
import '@theme/st-permalink'; // Buy it now carries the cart's attributes to checkout

var stPackCounts = (window.__stPackCounts ||= new Map()); // product id -> count, across re-renders
var stPackForms = (window.__stPackForms ||= new Set()); // connected pickers, asked whether they take an add

if (!customElements.get('st-pack-picker')) {
  customElements.define(
    'st-pack-picker',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="range"]');
        this.number = this.querySelector('.st-pk__n');
        this.minus = this.querySelector('[data-st-step="-1"]');
        if (!this.number) return;
        this.packIndex = this.dataset.packIndex || '0';
        this.details = this.closest('.product-details, product-details, [data-product-details]') || this.parentElement;
        this.#load(this.querySelector('[data-st-packs]')?.textContent);
        this.querySelectorAll('.st-pk__tick, .st-pk__qp').forEach((t) =>
          t.addEventListener('click', () => this.#set(Number(t.dataset.size), {}))
        );
        this.#notes();
        this.noun = this.querySelector('.st-pk__noun');

        const start = stPackCounts.get(this.dataset.product) ?? Number(this.number?.value) ?? 1;
        this.hidden = false;
        this.#set(start, { quiet: true });

        this.input?.addEventListener('input', () => this.#set(this.#toCount(Number(this.input.value)), { from: 'slider' }));
        this.number?.addEventListener('input', () => {
          const v = Number(this.number.value);
          if (Number.isFinite(v) && v >= 1) this.#set(v, { from: 'number' });
        });
        this.number?.addEventListener('change', () => this.#set(Number(this.number.value) || 1, { up: true }));
        this.querySelectorAll('[data-st-step]').forEach((b) =>
          b.addEventListener('click', () => this.#set(this.count + Number(b.dataset.stStep), {}))
        );
        this.section = this.closest('.shopify-section') || document;
        this.section.addEventListener('shopify:product:select', this.#onSelect);
        stPackForms.add(this);
      }

      disconnectedCallback() {
        this.section?.removeEventListener('shopify:product:select', this.#onSelect);
        stPackForms.delete(this);
      }

      // The packs sold with the selected variant's other options (its size or colour), from the
      // block's JSON.
      #load(json) {
        try {
          this.data = JSON.parse(json || 'null');
        } catch (e) {
          this.data = null;
        }
        this.packs = (this.data?.packs || []).filter((p) => p.size > 0).sort((a, b) => a.size - b.size);
        const avail = this.packs.filter((p) => p.available);
        this.sellable = avail.length ? avail : this.packs;
        // The scale: the pack sizes, evenly spaced as in v5 (1, 5, 10, 50, 100, 500), with every
        // count between neighbours reachable: single steps up to 50, then 5s, then 10s.
        this.anchors = [...new Set(this.packs.map((p) => p.size))].sort((a, b) => a - b);
        if (this.anchors[0] !== 1) this.anchors.unshift(1);
        this.S = 100;
        if (this.input) {
          this.input.min = '0';
          this.input.max = String(Math.max(1, this.anchors.length - 1) * this.S);
          this.input.step = '1';
        }
      }

      // A size or colour chosen in Horizon's picker. Horizon morphs only its picker, so the packs
      // above, and every price and cart line made from them, stayed those of the old size or colour
      // (code review 2026-10-16: a Medium stand priced and added to the cart as the Small one). Take
      // this block's packs from the section Horizon fetched for the new variant and price the same
      // count again; the pack total skips its own refresh while a counter prices it.
      #onSelect = (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog')) return;
        event.promise
          ?.then(({ detail }) => {
            const fresh = detail?.html?.querySelector(`st-pack-picker[data-block="${CSS.escape(this.dataset.block || '')}"]`);
            const json = fresh?.querySelector('[data-st-packs]')?.textContent;
            if (!json || !this.isConnected) return;
            const own = this.querySelector('[data-st-packs]');
            if (own) own.textContent = json;
            if (fresh.dataset.current) this.dataset.current = fresh.dataset.current;
            this.#load(json);
            this.#notes();
            this.#set(this.count, {});
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[st-pack-picker]', error);
          });
      };

      // Cheapest mix of packs for n units; ties go to fewer lines.
      mix(n) {
        const packs = this.sellable;
        const cost = new Array(n + 1).fill(Infinity);
        const lines = new Array(n + 1).fill(Infinity);
        const pick = new Array(n + 1).fill(-1);
        cost[0] = 0;
        lines[0] = 0;
        for (let k = 1; k <= n; k++) {
          packs.forEach((p, j) => {
            if (p.size > k || cost[k - p.size] === Infinity) return;
            const c = cost[k - p.size] + p.price;
            const l = lines[k - p.size] + 1;
            if (c < cost[k] || (c === cost[k] && l < lines[k])) {
              cost[k] = c;
              lines[k] = l;
              pick[k] = j;
            }
          });
        }
        if (cost[n] === Infinity) return null;
        const used = new Map();
        for (let k = n; k > 0; k -= packs[pick[k]].size) {
          const p = packs[pick[k]];
          used.set(p, (used.get(p) || 0) + 1);
        }
        return { total: cost[n], items: [...used].sort((a, b) => b[0].size - a[0].size).map(([p, q]) => ({ pack: p, quantity: q })) };
      }

      #set(value, { from, quiet, up } = {}) {
        // Counts start at the smallest pack (keychains come in 3s), and a count no mix of packs can
        // make (4 keychains, from packs of 3, 5 and 10) moves on to the next one that can be made,
        // in the direction of travel (up for a typed count), so the price and the buttons always have
        // a mix to show.
        const least = this.packs[0]?.size || 1;
        let n = Math.max(least, Math.min(9999, Math.round(Number(value) || least)));
        let dir = !up && n < (this.count ?? n) ? -1 : 1;
        for (let k = 0; k < 100 && !this.mix(n); k++) {
          if (n <= least) dir = 1;
          n = Math.max(least, n + dir);
        }
        if (this.number) this.number.min = String(least);
        this.count = n;
        stPackCounts.set(this.dataset.product, n);
        if (from !== 'number' && this.number) this.number.value = String(n);
        // The field is as wide as its digits, so the number sits at the counter's exact centre.
        this.number?.style.setProperty('--st-dg', String(Math.max(1, String(this.number.value).length)));
        if (this.minus) this.minus.disabled = n <= least;
        const noun = n === 1 ? this.data?.singular || '' : this.data?.plural || '';
        if (this.noun) this.noun.textContent = noun;
        if (this.input) {
          if (from !== 'slider') this.input.value = String(this.#toPos(n));
          this.input.setAttribute('aria-valuetext', `${n} ${noun}`.trim());
          this.input.style.setProperty('--st-fill', `${(Number(this.input.value) / Number(this.input.max)) * 100}%`);
        }
        this.querySelectorAll('.st-pk__tick').forEach((t) => t.toggleAttribute('aria-current', Number(t.dataset.size) === n));
        this.querySelectorAll('.st-pk__qp').forEach((q) => q.setAttribute('aria-pressed', String(Number(q.dataset.size) === n)));
        this.result = this.mix(n);
        // At the largest pack and beyond, offer the quote (blocks/st-pack-total.liquid, parity review
        // 2026-10-09: the 500 pack ended in nothing).
        this.details?.querySelectorAll('[data-st-bulk]').forEach((el) => (el.hidden = n < Number(el.dataset.stBulk)));
        this.#render(quiet);
        this.#stack(n, quiet);
        const detail = this.mixDetail();
        if (detail) {
          this.dispatchEvent(new CustomEvent('st:pack-mix', { bubbles: true, detail: { ...detail, initial: !this.announced } }));
          this.announced = true;
        }
      }

      // The count and its mix, for the tracker (assets/st-track.js) and a platform swap's event.
      mixDetail() {
        const r = this.result;
        if (!r) return null;
        return {
          product: this.dataset.product || '',
          title: this.dataset.title || '',
          variant: this.dataset.current || '',
          currency: this.data?.currency || '',
          count: this.count,
          total: r.total,
          items: r.items.map((it) => ({ id: it.pack.id, quantity: it.quantity, price: it.pack.price, label: it.pack.label })),
        };
      }

      // Each quick pick's second line: the pack's saving against single units, or the smallest
      // pack's price, so every pick has two lines (as on the homepage).
      #notes() {
        const d = this.data || {};
        const base = this.packs[0];
        this.querySelectorAll('.st-pk__qp').forEach((q) => {
          const size = Number(q.dataset.size);
          const r = this.mix(size);
          let pct = 0;
          if (base && r && size > base.size) pct = Math.round(100 - (r.total * 100) / ((base.price / base.size) * size));
          const label = `${size} ${size === 1 ? d.singular || '' : d.plural || ''}`.trim();
          // A ladder row's own text (count, price each, total) is its name; a tile needs the label.
          if (!q.querySelector('[data-st-qp-unit]')) q.setAttribute('aria-label', pct > 0 ? `${label}, ${(d.save || '').replace('[percent]', pct)}` : label);
          // Ladder rows: the price per unit and the total, with the saving kept for the chosen row.
          const unit = q.querySelector('[data-st-qp-unit]');
          if (unit && r) unit.innerHTML = (d.unit || '').replace('[unit]', money(r.total / size, d.money, d.currency)).replace('[noun]', d.singular || '');
          const tot = q.querySelector('[data-st-qp-total]');
          if (tot && r) tot.innerHTML = money(r.total, d.money, d.currency);
          const save = q.querySelector('[data-st-qp-save]');
          if (save) save.textContent = pct > 0 ? (d.save || '').replace('[percent]', pct) : '';
          const note = q.querySelector('[data-st-qp-note]');
          if (!note) return;
          note.classList.toggle('is-price', pct <= 0);
          note.textContent = pct > 0 ? `\u2212${pct}%` : base && size === base.size ? money(base.price, d.money, d.currency).replace(/<[^>]*>/g, '') : '';
        });
      }

      #toCount(v) {
        const k = Math.min(this.anchors.length - 2, Math.floor(v / this.S));
        if (k < 0) return this.anchors[0];
        const a = this.anchors[k];
        const b = this.anchors[k + 1];
        const raw = a + ((v - k * this.S) / this.S) * (b - a);
        const step = raw < 50 ? 1 : raw < 100 ? 5 : 10;
        return Math.min(b, Math.max(a, Math.round(raw / step) * step));
      }

      #toPos(n) {
        const A = this.anchors;
        if (n >= A[A.length - 1]) return (A.length - 1) * this.S;
        for (let k = 0; k < A.length - 1; k++) {
          if (n <= A[k + 1]) return Math.round(k * this.S + ((n - A[k]) / (A[k + 1] - A[k])) * this.S);
        }
        return 0;
      }

      // v5's stack on the product photo: past 5, 10 and 50 units, one, two and three more cards
      // fan out behind the first photo, which steps back; a "×N" badge sits on the gallery.
      #stack(n, quiet) {
        const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
        const media = gallery?.querySelector('slideshow-slide .product-media');
        const img = media?.querySelector('img');
        if (!gallery || !media || !img) return;
        let stk = media.querySelector('.st-stk');
        if (!stk) {
          stk = document.createElement('span');
          stk.className = 'st-stk';
          stk.setAttribute('aria-hidden', 'true');
          for (let i = 3; i >= 1; i--) {
            const ly = document.createElement('img');
            ly.className = `st-stk__ly st-stk__ly--${i}`;
            ly.alt = '';
            ly.decoding = 'async';
            stk.appendChild(ly);
          }
          media.prepend(stk);
          media.classList.add('st-stk-host');
        }
        const src = img.currentSrc || img.src;
        stk.querySelectorAll('img').forEach((ly) => {
          if (ly.src !== src) ly.src = src;
        });
        media.dataset.stLayers = String(n >= 50 ? 3 : n >= 10 ? 2 : n >= 5 ? 1 : 0);
        const box = gallery.querySelector('slideshow-container') || gallery;
        let badge = box.querySelector('.st-qbadge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'st-qbadge';
          badge.setAttribute('aria-hidden', 'true');
          box.appendChild(badge);
        }
        const text = `×${new Intl.NumberFormat(document.documentElement.lang || 'en').format(n)}`;
        if (badge.textContent !== text) {
          badge.textContent = text;
          if (!quiet) {
            badge.classList.remove('is-pop');
            void badge.offsetWidth;
            badge.classList.add('is-pop');
          }
        }
      }

      // The pack total and Buy it now follow the count.
      #render(quiet) {
        const total = this.details?.querySelector('st-pack-total');
        const r = this.result;
        const d = this.data || {};
        this.#buyNow();
        this.#preorder();
        if (!total || !r) return;
        const now = total.querySelector('.st-pk-total__now');
        const each = total.querySelector('.st-pk-total__each');
        const from = Number(now?.dataset.cents);
        if (now) {
          now.dataset.cents = String(r.total);
          now.innerHTML = money(r.total, d.money, d.currency);
          if (!quiet) countUp(now, from, d.money, d.currency);
        }
        const n = this.count;
        const base = this.packs[0];
        const parts = [];
        let was = total.querySelector('.st-pk-total__was');
        let wasHtml = '';
        if (base && n > base.size) {
          const singles = Math.round((base.price / base.size) * n);
          const percent = Math.round(100 - (r.total * 100) / singles);
          const unit = money(r.total / n, d.money, d.currency);
          if (percent > 0) {
            parts.push(`<span class="st-pk-total__save">${(d.save || '').replace('[percent]', percent)}</span>`);
            wasHtml = `<span class="visually-hidden">${d.was || ''}</span>${money(singles, d.money, d.currency)}`;
          }
          parts.push(`<span>${(d.unit || '').replace('[unit]', unit).replace('[noun]', d.singular || '')}</span>`);
        } else {
          parts.push(`<span>${(d.for || '').replace('[label]', `${n} ${n === 1 ? d.singular : d.plural}`)}</span>`);
        }
        if (each) each.innerHTML = parts.join('');
        if (wasHtml) {
          if (!was) {
            was = document.createElement('s');
            was.className = 'st-pk-total__was';
            now?.after(was);
          }
          was.innerHTML = wasHtml;
        } else was?.remove();
        if (now && !now.parentElement.classList.contains('st-pk-total__row')) {
          const row = document.createElement('div');
          row.className = 'st-pk-total__row';
          now.before(row);
          row.append(now);
          if (was) row.append(was);
        } else if (was && was.parentElement !== now?.parentElement) now?.parentElement.append(was);
        let mixEl = total.querySelector('.st-pk-total__mix');
        const showMix = r.items.length > 1 || r.items[0].quantity > 1;
        if (showMix) {
          if (!mixEl) {
            mixEl = document.createElement('p');
            mixEl.className = 'st-pk-total__mix';
            total.appendChild(mixEl);
          }
          mixEl.textContent = (d.mix || '[list]').replace('[list]', r.items.map((it) => `${it.quantity} × ${it.pack.label}`).join(' + '));
        } else mixEl?.remove();
      }

      // Native preorder (Matt, 2026-10-10): when the mix holds a pack at or below zero stock that keeps
      // selling, the button says Preorder, a line says when it ships, the lines carry a Preorder
      // property for fulfilment, and Buy it now is hidden (a cart permalink cannot carry a property).
      #preorder() {
        const d = this.data || {};
        const on = Boolean(this.result?.items.some((it) => it.pack.preorder));
        this.preorderOn = on;
        const btn = [...(this.details?.querySelectorAll('button[name="add"]') || [])].find((b) => !b.closest('st-sticky-buy'));
        const label = btn?.querySelector('.add-to-cart-text__content') || btn;
        if (label) {
          if (!label.dataset.stLabel) label.dataset.stLabel = label.textContent.trim();
          const want = on ? d.preorderLabel || label.dataset.stLabel : label.dataset.stLabel;
          if (label.textContent.trim() !== want) label.textContent = want;
        }
        let note = this.details?.querySelector('.st-pk-pre');
        if (on) {
          if (!note) {
            note = document.createElement('p');
            note.className = 'st-pk-pre';
            note.setAttribute('role', 'status');
            this.details?.querySelector('st-pack-total')?.after(note);
          }
          const text = d.preorderNote || d.preorderDefault || '';
          if (note && note.textContent !== text) note.textContent = text;
        } else note?.remove();
        const buy = this.details?.querySelector('.st-pk-buy');
        if (buy) buy.hidden = on;
      }

      // Horizon's Buy it now buys one variant; this link buys the mix, through a cart permalink.
      #buyNow() {
        const accel = this.details?.querySelector('.accelerated-checkout-block');
        if (!accel) return;
        accel.hidden = true;
        let link = this.details.querySelector('.st-pk-buy');
        if (!link) {
          link = document.createElement('a');
          link.className = 'st-pk-buy';
          link.dataset.stPermalink = '';
          link.textContent = this.data?.buy || '';
          accel.after(link);
        }
        const root = window.Shopify?.routes?.root || '/';
        const r = this.result;
        if (r) link.href = `${root}cart/${r.items.map((it) => `${it.pack.id}:${it.quantity}`).join(',')}`;
        link.toggleAttribute('aria-disabled', !r);
      }

      // Called for an Add to cart in the same product details. True when it handled the add.
      handles(form) {
        return Boolean(this.details?.contains(form) && this.result);
      }

      async add(form, button) {
        const err = this.querySelector('.st-pk__error');
        if (err) err.hidden = true;
        button?.setAttribute('aria-busy', 'true');
        if (button) button.disabled = true;
        const root = window.Shopify?.routes?.root || '/';
        const json = (res) => {
          if (!res.ok) throw new Error(String(res.status));
          return res.json();
        };
        try {
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId);
          const d = this.data || {};
          const items = this.result.items.map((it) => ({
            id: it.pack.id,
            quantity: it.quantity,
            ...(it.pack.preorder && d.preorderProp ? { properties: { [d.preorderProp]: d.preorderValue || '' } } : {}),
          }));
          const added = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}) }),
          }).then(json);
          const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(json);
          const deferred = CartLinesUpdateEvent.createPromise();
          form.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
              promise: deferred.promise,
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { sections: added.sections, items: cart.items, itemCount: cart.item_count, source: 'st-pack-picker', didError: false },
          });
        } catch (e) {
          if (err) {
            err.textContent = this.data?.error || '';
            err.hidden = false;
          }
        } finally {
          button?.removeAttribute('aria-busy');
          if (button) button.disabled = false;
        }
      }
    }
  );
}

// Add to cart: taken before Horizon's handler (it listens on document, in capture), only when the
// count is not simply the selected variant once.
if (!window.__stPackSubmit) {
  window.__stPackSubmit = true;
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
      const picker = [...stPackForms].find((p) => p.handles(form));
      if (!picker) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      picker.add(form, event.submitter || form.querySelector('[type="submit"]'));
    },
    { capture: true }
  );
}

/* The pack total (blocks/st-pack-total.liquid). Horizon's variant change fetches the section and
   resolves the select event's promise with the parsed HTML; take this block's new copy from it. */
if (!customElements.get('st-pack-total')) {
  customElements.define(
    'st-pack-total',
    class extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section') || document;
        this.section.addEventListener('shopify:product:select', this.#onSelect);
      }

      disconnectedCallback() {
        this.section?.removeEventListener('shopify:product:select', this.#onSelect);
      }

      #onSelect = (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
        // A pack count in the same product details prices this total from the count and follows
        // the variant change itself; the server's copy would show one pack, not the count.
        if ([...stPackForms].some((p) => p.details?.contains(this))) return;
        this.setAttribute('aria-busy', 'true');
        event.promise
          ?.then(({ detail }) => {
            const fresh = detail?.html?.querySelector(`st-pack-total[data-block="${CSS.escape(this.dataset.block)}"]`);
            if (!fresh) return;
            const from = Number(this.querySelector('[data-cents]')?.dataset.cents);
            this.replaceChildren(...[...fresh.childNodes].map((node) => document.importNode(node, true)));
            countUp(this.querySelector('[data-cents]'), from, this.dataset.money, this.dataset.currency);
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[st-pack-total]', error);
          })
          .finally(() => {
            // A count in progress clears aria-busy itself when it lands on the final price.
            if (!this.querySelector('[data-counting]')) this.removeAttribute('aria-busy');
          });
      };
    }
  );
}
